import { Parcel } from "./Parcel";
import { Gameplay, TIMER } from "../Gameplay";
import { ParcelManager } from "./ParcelManager";
import { GameWorld } from "../Game World/GameWorld";
import { Character } from "../Player/Character";
import { Player } from "../Player/Player";
import { LevelManager } from "../Levels";



const enum eState {
    idle,
    refilling,
}



export class Dispenser {
    /**
     * Velikost oblasti pro sebrani balicku na ose Z
     */
    static readonly PICKUP_AREA_SIZE = 0.15;

    /**
     * Offset zacatku pasu od stredu cesty
     */
    private static readonly BELT_X_OFFSET = 0.625;

    /**
     * Velikost grafiky pasu
     */
    private static readonly BELT_SIZE = 0.5;

    /**
     * Pozice baliku na ose Y, pokud je ve vydejne
     */
    private static readonly PARCEL_Y = 0.18;

    /**
     * (RO) Index vydejny
     */
    get id(): number { return this._id; }
    private _id: number;

    /**
     * (RO) Pozice vydejny v hernim svete na ose Z
     */
    get worldPos(): number { return this._worldPos; }
    private _worldPos: number;

    /**
     * (RO) Smer vydeje balicku na ose X (-1 = balicky se vydavaji z prave strany cesty, 1 = z leve)
     */
    get dir(): number { return this._dir; }
    private _dir: number;

    /**
     * Aktualni stav vydejny
     */
    private _state: eState;

    private _time: number;

    /**
     * Bunky posuvniku (bunka s indexem 0 je bunka nejbliz k ceste)
     */
    private _beltCells: Parcel[];

    /**
     * Velikost bunky
     */
    private _beltCellSize: number;

    /**
     * Pocet pripravenych baliku na pasu
     */
    private _prepParcelCnt: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(id: number, worldPos: number, dir: number) {
        this._id = id;
        this._worldPos = worldPos;
        this._dir = dir;

        // Bunky pasu
        let cellCnt = LevelManager.INSTANCE.lvlSettings.player.charCnt;

        this._beltCellSize = Dispenser.BELT_SIZE / cellCnt;
        this._beltCells = [];

        let i = cellCnt * 2;

        while (i-- != 0)
            this._beltCells.push(undefined);

        // Zachytavat smrt postavy hrace
        Gameplay.instance.events.on(Character.EVENT_DEATH, this.handlePlCharDeath, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    release(): void {
        // Odstranit balicky
        this.removeParcels();

        // Ukoncit sledovani udalosti
        Gameplay.instance.events.off(Character.EVENT_DEATH, this.handlePlCharDeath, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        // Pripravit balicky na pasu
        this.removeParcels();

        let freeParcels = ParcelManager.instance.freeParcels;
        let j = this._beltCells.length / 2;

        for (let i = 0; i < j; i++) {
            let x = this.getCellParcelPos(i);
            let parcel = freeParcels.getItem();

            parcel.prepareForPickup(x, Dispenser.PARCEL_Y, this._worldPos);
            parcel.entity.getPosition();

            this._beltCells[i] = parcel;
        }

        this._prepParcelCnt = j;
        this._state = eState.idle;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._state == eState.refilling) {
            let progress = (TIMER.time - this._time) / 200;

            if (progress > 1)
                progress = 1;

            // Ziskat pozici baliku v posledni bunce
            let x = this.getCellParcelPos(this._beltCells.length - 1) + (this._beltCellSize * progress * this._dir);

            for (let cellId = this._beltCells.length - 1; cellId > 0; cellId--) {
                let parcel = this._beltCells[cellId];

                if (parcel)
                    parcel.entity.setPosition(x, Dispenser.PARCEL_Y, this._worldPos);

                x += this._beltCellSize * this._dir;
            }

            if (progress == 1) {
                // Posunout bunky pole
                for (let i = 1; i < this._beltCells.length; i++)
                    this._beltCells[i - 1] = this._beltCells[i];

                this._beltCells[this._beltCells.length - 1] = undefined;

                if (!this._beltCells[0]) {
                    this._time = TIMER.time;
                }
                else {
                    this._state = eState.idle;
                }
            }
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vyzvednuti balicku postavou hrace
     * @param plChar 
     */
    //-------------------------------------------------------------------------
    pickupParcel(plChar: Character): Parcel {
        if (this._prepParcelCnt == 0)
            return undefined;

        // Ziskat prvni balicek
        let cellId = 0;

        while (!this._beltCells[cellId])
            cellId++;

        let parcel = this._beltCells[cellId];

        parcel.pickup(plChar, plChar.packageCnt);

        this._beltCells[cellId] = undefined;
        this._prepParcelCnt--;

        return parcel;
    }

    //-------------------------------------------------------------------------
    /**
     * Doplneni balicku podle poctu zivych postav hrace
     */
    //-------------------------------------------------------------------------
    refillParcels(): void {
        let newParcelCnt = Player.instance.liveCharCnt - this._prepParcelCnt;

        if (newParcelCnt != 0) {
            for (let i = 0; i < newParcelCnt; i++) {
                let parcel = ParcelManager.instance.freeParcels.getItem();
                let cellId = this._beltCells.length / 2 + i;

                this._beltCells[cellId] = parcel;
                parcel.prepareForPickup(this.getCellParcelPos(cellId), Dispenser.PARCEL_Y, this._worldPos);

                this._prepParcelCnt++;
            }

            this._state = eState.refilling;
            this._time = TIMER.time;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Uvolni vsechny balicky ve vydejne
     */
    //-------------------------------------------------------------------------    
    private removeParcels(): void {
        this._beltCells.forEach((parcel: Parcel, i: number) => {
            if (parcel) {
                parcel.reset();
                this._beltCells[i] = undefined;
            }
        });
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci pozici balicku na ose X podle zadaneho indexu bunky
     * @param cellId 
     */
    //-------------------------------------------------------------------------
    private getCellParcelPos(cellId: number): number {
        return (GameWorld.PATH_X + GameWorld.PATH_WIDTH / 2) - (Dispenser.BELT_X_OFFSET + (cellId * this._beltCellSize) + (this._beltCellSize / 2)) * this._dir;
    }

    //-------------------------------------------------------------------------
    /**
     * Zpracovat smrt postavy hrace
     * @param char
     */
    //-------------------------------------------------------------------------
    private handlePlCharDeath(char: Character): void {
        // Pokud postava nenese balicek z teto vydejny, tak odstranit posledni balicek na pasu
        if (!char.hasPackageFromDispenser(this)) {
            let cellId = this._beltCells.length - 1;

            while (!this._beltCells[cellId])
                cellId--;

            this._beltCells[cellId].discard();
            this._beltCells[cellId] = undefined;

            this._prepParcelCnt--;
        }
    }
}